import Button from "@edaz/edazkit/Button/index";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { Check, Input } from "@frui.ts/bootstrap";
import { preventDefault } from "@frui.ts/views";
import DatePickerBox from "controls/datePickerBox";
import LicensePlateInput from "controls/licensePlateInput";
import { observer } from "mobx-react-lite";
import React from "react";
import { ButtonToolbar } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { isFilterDirty, isFilterEmpty } from "utils/filters";
import { translateGeneral } from "utils/localizations";
import type PairingByOrderListViewModel from "viewModels/finance/paymentsPairing/pairingByOrder/pairingByOrderListViewModel";
import type PairingByPaymentOrdersViewModel from "viewModels/finance/paymentsPairing/pairingByPayment/pairingByPaymentOrdersViewModel";
import CountrySelect from "../../common/countrySelect";

export interface OrderFiltersProps {
  vm: PairingByOrderListViewModel | PairingByPaymentOrdersViewModel;
}

const OrderFilters = observer((props: OrderFiltersProps) => {
  const { vm } = props;
  const fullTextClassNames = vm.filter.fullTextSearchEnabled ? "border-success border-2" : "";

  return (
    <Form className="mb-4" onSubmit={preventDefault(vm.applyFilterAndLoad)} onReset={preventDefault(vm.resetFilterAndLoad)}>
      <div className="row">
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("charge", "vehicle")}</Form.Label>
          <LicensePlateInput
            target={vm.filter}
            property="licensePlate"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("orderIdent")}</Form.Label>
          <Input target={vm.filter} property="orderIdent" step={0.01} placeholder={translateGeneral("search")} type="number" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("charge", "emailAddress")}</Form.Label>
          <Input target={vm.filter} property="email" placeholder={translateGeneral("search")} className={fullTextClassNames} />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("charge", "phoneNumber")}</Form.Label>
          <Input
            target={vm.filter}
            property="phoneNumber"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("licensePlate", "countryId")}</Form.Label>
          <CountrySelect
            target={vm.filter}
            property="countryId"
            vm={vm.countrySelectViewModel}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("status")}</Form.Label>
          <AdvancedSelect
            target={vm.filter}
            property="orderStatus"
            items={vm.orderStateOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("createdAt")}</Form.Label>
          <DatePickerBox target={vm.filter} property="orderCreatedDate" placeholder={translateGeneral("search")} />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("charge", "chargeTypeId")}</Form.Label>
          <AdvancedSelect
            target={vm.filter}
            property="validFor"
            items={vm.chargeValidityOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("payment", "amount")}</Form.Label>
          <Input target={vm.filter} property="paidAmount" step={0.01} placeholder={translateGeneral("search")} type="number" />
        </Form.Group>
        <div className="col col-m-6 col-lg-4 col-xl-3">
          <div className="row">
            <Form.Group as={Col} md={6}>
              <Form.Label>{vm.translateSubAttribute("charge", "validSince")}</Form.Label>
              <DatePickerBox target={vm.filter} property="validSinceDate" placeholder={translateGeneral("search")} />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>{vm.translateSubAttribute("charge", "validUntil")}</Form.Label>
              <DatePickerBox target={vm.filter} property="validUntilDate" placeholder={translateGeneral("search")} />
            </Form.Group>
          </div>
        </div>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateAttribute("sourceType")}</Form.Label>
          <AdvancedSelect
            target={vm.filter}
            property="sourceType"
            items={vm.sourceTypeOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateSubAttribute("payment", "paymentType")}</Form.Label>
          <AdvancedSelect
            target={vm.filter}
            property="paymentType"
            items={vm.paymentTypeOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
      </div>
      <ButtonToolbar className="mt-2 align-items-center">
        <Button size="sm" type="submit" disabled={isFilterEmpty(vm.filter) || !isFilterDirty(vm.filter)}>
          {translateGeneral("search")}
        </Button>
        {(isFilterDirty(vm.filter) || !isFilterEmpty(vm.filter)) && (
          <Button size="sm" type="reset" variant="outline-danger">
            {translateGeneral("resetFilter")}
          </Button>
        )}
        {!isFilterEmpty(vm.filter) && (
          <Check
            className="ml-sm-4 mt-sm-0 mt-4"
            target={vm.filter}
            property="fullTextSearchEnabled"
            custom
            label={vm.translate("allowFullTextSearch")}
          />
        )}
      </ButtonToolbar>
    </Form>
  );
});

export default OrderFilters;
